import React, { useState, useEffect } from 'react';
import './DynamicMarriageInformationTable.css';
import DateInput from './DateInput';

interface MarriageEntry {
  maritalStatus: string;
  dateOfMarriage: string;
  spouseLastName: string;
  spouseGivenName: string;
  spouseDateOfBirth: string;
  spouseCountryOfBirth: string;
  spousePresentAddress: string;
  spouseOccupation: string;
}

interface DynamicMarriageInformationTableProps {
  value: { [key: string]: MarriageEntry };
  onChange: (entries: { [key: string]: MarriageEntry }) => void;
  required?: boolean;
}

const DynamicMarriageInformationTable: React.FC<DynamicMarriageInformationTableProps> = ({
  value,
  onChange,
  required = false
}) => {
  const [entries, setEntries] = useState<{ [key: string]: MarriageEntry }>(value || {});
  const [hasPreviousMarriages, setHasPreviousMarriages] = useState<boolean>(false);

  useEffect(() => {
    onChange(entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]); // Only depend on entries, not onChange to prevent infinite loops

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter' && hasPreviousMarriages) {
        addEntry();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasPreviousMarriages]);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `marriage${entryCount + 1}`;
    
    const newEntry: MarriageEntry = {
      maritalStatus: '',
      dateOfMarriage: '',
      spouseLastName: '',
      spouseGivenName: '',
      spouseDateOfBirth: '',
      spouseCountryOfBirth: '',
      spousePresentAddress: '',
      spouseOccupation: ''
    };
    
    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;
    
    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: MarriageEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();
    
    remainingKeys.forEach((oldKey, index) => {
      const newKey = `marriage${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });
    
    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (key: string, field: keyof MarriageEntry, value: string) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value }
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const handlePreviousMarriagesChange = (hasPrevious: boolean) => {
    setHasPreviousMarriages(hasPrevious);
    if (!hasPrevious && Object.keys(entries).length > 1) {
      // Keep only the first entry if user selects "No"
      const firstKey = Object.keys(entries).sort()[0];
      const firstEntry = entries[firstKey];
      const updatedEntries = { [firstKey]: firstEntry };
      setEntries(updatedEntries);
      onChange(updatedEntries);
    }
  };

  // Initialize with at least one entry if empty
  useEffect(() => {
    if (Object.keys(entries).length === 0) {
      addEntry();
    }
  }, []);

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="marriage-information-table">
      <div className="table-header">
        <h4>Marriage Information</h4>
        {hasPreviousMarriages && (
          <button
            type="button"
            className="add-btn"
            onClick={addEntry}
            title="Add marriage entry (Ctrl+Enter)"
          >
            + Add Marriage Entry
          </button>
        )}
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No marriage information entered yet.</p>
          <button
            type="button"
            className="add-first-btn"
            onClick={addEntry}
          >
            Add First Marriage Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Marriage Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this marriage entry"
                >
                  ×
                </button>
              </div>
              
              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Marital Status</label>
                    <select
                      value={entries[key].maritalStatus}
                      onChange={(e) => updateEntry(key, 'maritalStatus', e.target.value)}
                    >
                      <option value="">Select status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Separated">Separated</option>
                    </select>
                  </div>
                  
                  <div className="field-group">
                    <label>Date of Marriage</label>
                    <input
                      type="date"
                      value={entries[key].dateOfMarriage}
                      onChange={(e) => updateEntry(key, 'dateOfMarriage', e.target.value)}
                      placeholder="yyyy-mm-dd"
                    />
                  </div>
                </div>
                
                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>Spouse Last Name</label>
                    <input
                      type="text"
                      value={entries[key].spouseLastName}
                      onChange={(e) => updateEntry(key, 'spouseLastName', e.target.value)}
                      placeholder="Last name"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Spouse Given Name</label>
                    <input
                      type="text"
                      value={entries[key].spouseGivenName}
                      onChange={(e) => updateEntry(key, 'spouseGivenName', e.target.value)}
                      placeholder="Given name"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Spouse Date of Birth</label>
                    <input
                      type="date"
                      value={entries[key].spouseDateOfBirth}
                      onChange={(e) => updateEntry(key, 'spouseDateOfBirth', e.target.value)}
                      placeholder="yyyy-mm-dd"
                    />
                  </div>
                </div>
                
                <div className="form-row form-row-3">
                  <div className="field-group">
                    <label>Spouse Country of Birth</label>
                    <input
                      type="text"
                      value={entries[key].spouseCountryOfBirth}
                      onChange={(e) => updateEntry(key, 'spouseCountryOfBirth', e.target.value)}
                      placeholder="Country"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>Spouse Occupation</label>
                    <input
                      type="text"
                      value={entries[key].spouseOccupation}
                      onChange={(e) => updateEntry(key, 'spouseOccupation', e.target.value)}
                      placeholder="Occupation"
                    />
                  </div>
                </div>
                
                <div className="form-row form-row-4">
                  <div className="field-group">
                    <label>Spouse Present Address</label>
                    <textarea
                      value={entries[key].spousePresentAddress}
                      onChange={(e) => updateEntry(key, 'spousePresentAddress', e.target.value)}
                      placeholder="Present address"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entryKeys.length > 0 && (
        <div className="table-footer">
          <p className="add-hint">Need to add more marriage entries?</p>
          <button 
            type="button" 
            className="add-another-btn"
            onClick={addEntry}
          >
            + Add Another Marriage Entry
          </button>
        </div>
      )}
    </div>
  );
};

export default DynamicMarriageInformationTable; 